import { Activity, Banknote, FileText, ShieldCheck, type LucideIcon } from 'lucide-react';
import { LoadingSkeleton } from './LoadingSkeleton';

export interface TimelineEvent {
  type: string; // analytics | credit | processo | audit | ...
  at: string; // ISO datetime
  label: string;
  detail?: string | null;
  meta?: Record<string, unknown>;
}

interface Props {
  events: TimelineEvent[];
  loading?: boolean;
  empty?: string;
  className?: string;
}

const ICONS: Record<string, LucideIcon> = {
  analytics: Activity,
  credit: Banknote,
  processo: FileText,
  audit: ShieldCheck,
};

const TONE_BG: Record<string, string> = {
  analytics: 'bg-emerald-600/20 text-emerald-300',
  credit: 'bg-amber-600/20 text-amber-300',
  processo: 'bg-blue-600/20 text-blue-300',
  audit: 'bg-purple-600/20 text-purple-300',
};

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function TimelineList({
  events,
  loading,
  empty = 'Sem eventos para mostrar.',
  className = '',
}: Props) {
  if (loading) {
    return (
      <div className={className}>
        <LoadingSkeleton rows={5} />
      </div>
    );
  }
  if (!events || events.length === 0) {
    return <div className={`text-sm text-slate-400 ${className}`}>{empty}</div>;
  }

  return (
    <ol className={`space-y-3 ${className}`}>
      {events.map((e, i) => {
        const Icon = ICONS[e.type] ?? Activity;
        const tone = TONE_BG[e.type] ?? 'bg-slate-700/40 text-slate-300';
        return (
          <li key={`${e.type}-${e.at}-${i}`} className="flex gap-3">
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tone}`}
              aria-hidden="true"
            >
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-sm font-medium text-slate-100">
                  {e.label}
                  <span className="ml-2 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                    {e.type}
                  </span>
                </p>
                <time className="text-xs text-slate-500" dateTime={e.at}>
                  {formatDateTime(e.at)}
                </time>
              </div>
              {e.detail && (
                <p className="mt-0.5 text-xs text-slate-400">{e.detail}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
